import { useCompanySettings, usePatchCompanySettings } from "@/hooks/useCompanySettings";
import useTranslation from "@/hooks/useTranslation";
import styles from "@/pages/settings/settings.module.scss";
import { Col, DatePicker, message, Popconfirm, Row, Space, Switch } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { RangeValueType } from "rc-picker/lib/PickerInput/RangePicker";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import React from "react";

const { RangePicker } = DatePicker;
function ManageScope() {
  const { data: companySettings, isLoading } = useCompanySettings();
  const { year, updateYear } = useDashboardYear();
  const [rangeValue, setRangeValue] = React.useState<RangeValueType<Dayjs>>([
    dayjs(String(companySettings?.reporting_start_year), "YYYY"),
    dayjs(String(companySettings?.reporting_end_year), "YYYY"),
  ]);
  const [showRangeTooltip, setShowRangeTooltip] = React.useState(false);

  // const [monthValue, setMonthValue] = React.useState<Dayjs>(
  //   dayjs(String(companySettings?.fiscal_month), "M")
  // );
  // const [showMonthTooltip, setShowMonthTooltip] = React.useState(false);

  const patchEolCompanySettings = usePatchCompanySettings();
  const patchUsageCompanySettings = usePatchCompanySettings();

  const includeEol = companySettings?.include_eol || false;
  const includeUsage = companySettings?.include_usage || false;

  const translation = useTranslation();

  const onEolChange = async (value: boolean) => {
    await patchEolCompanySettings.mutateAsync({
      include_eol: value,
    });
    message.success(
      value
        ? translation.settings.impact.eolSwitchOnSuccess
        : translation.settings.impact.eolSwitchOffSuccess
    );
  };

  const onUsePhaseChange = async (value: boolean) => {
    await patchUsageCompanySettings.mutateAsync({
      include_usage: value,
    });
    message.success(
      value
        ? translation.settings.impact.usePhaseSwitchOnSuccess
        : translation.settings.impact.usePhaseSwitchOffSuccess
    );
  };

  const cancelRangeChange = () => {
    setShowRangeTooltip(false);
    setRangeValue([
      dayjs(String(companySettings?.reporting_start_year), "YYYY"),
      dayjs(String(companySettings?.reporting_end_year), "YYYY"),
    ]);
  };

  const confirmRangeChange = async () => {
    const reporting_start_year = rangeValue[0]?.year();
    const reporting_end_year = rangeValue[1]?.year();

    await patchUsageCompanySettings
      .mutateAsync({
        reporting_start_year: reporting_start_year,
        reporting_end_year: reporting_end_year,
      })
      .then(() => {
        message.success(translation.settings.impact.yearRangeChangeSuccess);
        setShowRangeTooltip(false);
        if (year && reporting_start_year && reporting_end_year) {
          if (year < reporting_start_year || year > reporting_end_year) {
            updateYear(reporting_start_year);
          }
        }
      });
  };

  const onRangeChange = (value: any) => {
    setRangeValue(value);
    setShowRangeTooltip(true);
  };

  // const confirmMonthChange = async () => {
  //   await patchUsageCompanySettings
  //     .mutateAsync({
  //       fiscal_month: monthValue.month() + 1,
  //     })
  //     .then(() => {
  //       message.success(translation.settings.impact.fiscalMonthChangeSuccess);
  //       setShowMonthTooltip(false);
  //     });
  // };

  // const cancelMonthChange = () => {
  //   setShowMonthTooltip(false);
  //   setMonthValue(dayjs(String(companySettings?.fiscal_month), "M"));
  // };

  // const onMonthChange = (value: any) => {
  //   setMonthValue(value);
  //   setShowMonthTooltip(true);
  // };

  return (
    <Row
      className={styles.content}
      gutter={[0, 20]}
    >
      <Col span={24}>
        <span className={styles.heading}>{translation.settings.impact.heading}</span>
      </Col>
      <Col span={24}>
        <span className={styles.description}>{translation.settings.impact.description}</span>
      </Col>

      <Col>
        <Row gutter={[18, 0]}>
          <Col span={24}>
            <span className={styles.heading}>Scope</span>
          </Col>
          <Col>
            <Space direction={"vertical"}>
              <span className={styles.description}>Data collection year range</span>
              <Popconfirm
                title="Are you sure?"
                description={
                  <div style={{ maxWidth: 300 }}>{translation.settings.impact.rangeTooltip}</div>
                }
                onConfirm={confirmRangeChange}
                onCancel={cancelRangeChange}
                okText="Yes"
                cancelText="No"
                open={showRangeTooltip}
                placement={"right"}
              >
                <RangePicker
                  picker="year"
                  allowClear={false}
                  value={rangeValue}
                  onChange={onRangeChange}
                />
              </Popconfirm>
            </Space>
          </Col>
          {/* <Col>
            <Space direction={"vertical"}>
              <span className={styles.description}>Beginning of reporting year</span>
              <Popconfirm
                title="Are you sure?"
                description={
                  <div style={{ maxWidth: 300 }}>
                    {translation.settings.impact.fiscalMonthTooltip}
                  </div>
                }
                onConfirm={confirmMonthChange}
                onCancel={cancelMonthChange}
                okText="Yes"
                cancelText="No"
                open={showMonthTooltip}
                placement={"right"}
              >
                <DatePicker
                  picker="month"
                  format={"MMM"}
                  value={monthValue}
                  allowClear={false}
                  onChange={onMonthChange}
                  popupClassName={"monthPicker"}
                />
              </Popconfirm>
            </Space>
          </Col> */}
        </Row>
      </Col>

      <Col>
        <Row>
          <Col span={24}>
            <span className={styles.heading}>Product lifecycle</span>
          </Col>
          <Col span={24}>
            <Space direction={"vertical"}>
              <span className={styles.description}>
                Include the product use phase & end of life in your scope?
              </span>
              <Space className={styles.switchBox}>
                <span className={styles.description}>Usage</span>
                <Switch
                  loading={isLoading || patchUsageCompanySettings.isLoading}
                  size={"small"}
                  value={includeUsage}
                  onChange={onUsePhaseChange}
                />
                <span className={styles.description}>End of life</span>

                <Switch
                  loading={isLoading || patchUsageCompanySettings.isLoading}
                  size={"small"}
                  value={includeEol}
                  onChange={onEolChange}
                />
              </Space>
            </Space>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

export default ManageScope;
